/**
 * PROJECT-055 — Deliverable / Process / Bot construction API
 * Wraps the post-wizard construction flow endpoints.
 *
 * Deliverable previews are stored in `entities.preferences[user_id].pending_deliverable_jobs`
 * (JSONB), not a dedicated table. Construction uses the PROJECT-052/053 backward composer.
 */

import AuthUtils from "@/providers/firebase/authUtils";
import { ApiResponse } from "./kernelBotApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────

export interface BuildContext {
  role?: string;
  goals?: string[];
  revenue_stage?: string;
  first_tasks?: string[];
  build_scope?: "light" | "standard" | "deep";
}

export interface Deliverable {
  deliverable_id: string;
  name: string;
  description: string;
  domain: string;
  focus: boolean;
  focus_reason: string;
  source: string;
  status: "proposed" | "approved" | "edited" | "rejected";
  edited?: Record<string, unknown> | null;
  feedback?: string | null;
  process_preview?: {
    purpose?: string;
    steps?: number;
    estimated_bot_count?: number;
  };
}

export interface BuildDeliverablesRequest {
  context?: BuildContext;
}

export interface BuildDeliverablesResponse {
  execution_id: string;
  status: string;
  stage: string;
  deliverables_count: number;
  process_ids?: string[];
}

export interface DeliverablesJobResponse {
  execution_id: string;
  status: string;
  stage: string;
  /**
   * The buyer's state and the sentence she reads — supplied by the BACKEND
   * (`deliverable_manager.py`, `@P288` §13 stage 6) and rendered verbatim.
   *
   * THIS FILE KEEPS NO MAP OF ITS OWN. A state→word map here would be a second
   * home for the ruled copy, which the corpus forbids (`MR0v`); the same reason
   * `setupRunApi.ts` renders the run's `state_label` without one. The BE is the
   * only authority for the wording, so a wording change is a BE change.
   *
   * Optional because only `GET /{entity_id}/deliverables` carries them — the
   * job route (`getJobDeliverables`) returns the machine's `status`/`stage`
   * alone. Declaring them required would be a lie about the other route.
   */
  state?: string;
  state_label?: string;
  deliverables: Deliverable[];
}

export interface ReviewDeliverableRequest {
  action: "approve" | "edit" | "reject";
  edited?: { name?: string; description?: string };
  feedback?: string;
}

export interface BatchConstructRequest {
  deliverable_ids?: string[];
}

export interface BatchConstructResponse {
  execution_id: string;
  status: string;
  stage: string;
  entity_id: string;
  process_ids: string[];
  failed: { deliverable_id: string; reason: string }[];
}

export interface ReviewProcessRequest {
  action: "approve" | "edit" | "reject";
  edited?: { processName?: string; steps?: unknown[] };
  feedback?: string;
}

export interface ReviewBotRequest {
  action: "approve" | "edit" | "reject";
  edited?: { botName?: string; kernel_keys_ref?: string[] };
  feedback?: string;
}

export interface ActivateRequest {
  process_ids?: string[];
}

export interface ActivateResponse {
  execution_id: string;
  status: string;
  stage: string;
  entity_id: string;
  entity_status: string;
  activated_processes: string[];
  activated_bots: string[];
  first_executions: {
    process_id: string;
    process_key: string;
    call_id: string | null;
    status: string;
    error?: string;
  }[];
}

// ── Helper ─────────────────────────────────────────────────────────

async function apiCall<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, "Content-Type": "application/json", ...options.headers },
  });
  return res.json();
}

// ── Deliverable endpoints ──────────────────────────────────────────

export async function buildDeliverables(
  entityId: string,
  request: BuildDeliverablesRequest = {},
): Promise<ApiResponse<BuildDeliverablesResponse>> {
  return apiCall<BuildDeliverablesResponse>(`/api/v1/entity/${entityId}/deliverables/build`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function getJobDeliverables(
  entityId: string,
  jobId: string,
): Promise<ApiResponse<DeliverablesJobResponse>> {
  return apiCall<DeliverablesJobResponse>(`/api/v1/entity/${entityId}/deliverables/job/${jobId}`);
}

export async function getCurrentDeliverables(
  entityId: string,
): Promise<ApiResponse<DeliverablesJobResponse>> {
  return apiCall<DeliverablesJobResponse>(`/api/v1/entity/${entityId}/deliverables`);
}

export async function reviewDeliverable(
  entityId: string,
  deliverableId: string,
  request: ReviewDeliverableRequest,
): Promise<ApiResponse<Deliverable>> {
  return apiCall<Deliverable>(`/api/v1/entity/${entityId}/deliverables/${deliverableId}/review`, {
    method: "PATCH",
    body: JSON.stringify(request),
  });
}

export async function batchConstructDeliverables(
  entityId: string,
  request: BatchConstructRequest = {},
): Promise<ApiResponse<BatchConstructResponse>> {
  return apiCall<BatchConstructResponse>(`/api/v1/entity/${entityId}/deliverables_2_processes`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function constructProcess(
  entityId: string,
  processId: string,
): Promise<ApiResponse<{ process_id: string; process_key: string; status: string; steps_count: number }>> {
  return apiCall<{ process_id: string; process_key: string; status: string; steps_count: number }>(
    `/api/v1/entity/${entityId}/deliverable_2_process`,
    {
      method: "POST",
    },
  );
}

// ── Process / Bot review (feedback only, not gates) ────────────────

export async function reviewProcess(
  processId: string,
  request: ReviewProcessRequest,
): Promise<ApiResponse<{ processId: string; processKey: string; status: string; versionNumber: number }>> {
  return apiCall(`/api/v1/process/${processId}/review`, {
    method: "PATCH",
    body: JSON.stringify(request),
  });
}

export async function reviewBot(
  botId: string,
  request: ReviewBotRequest,
): Promise<ApiResponse<{ botId: string; botKey: string; status: string; versionNumber: number }>> {
  return apiCall(`/api/v1/bot/${botId}/review`, {
    method: "PATCH",
    body: JSON.stringify(request),
  });
}

// ── Activation ─────────────────────────────────────────────────────

export async function activateEntity(
  entityId: string,
  request: ActivateRequest = {},
): Promise<ApiResponse<ActivateResponse>> {
  return apiCall<ActivateResponse>(`/api/v1/entity/${entityId}/drafts_2_live`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

// ── T0252 / T0704: Focused idea → process ──────────────────────────

export interface ProcessRecurrence {
  enabled: boolean;
  /** "daily" | "weekly" | "monthly" | "custom" */
  pattern: "daily" | "weekly" | "monthly" | "custom";
  /** Time of day in HH:MM (24h) */
  time: string;
  /** For daily: every N days; weekly: every N weeks; monthly: every N months */
  interval?: number;
  /** For weekly: 0 = Sunday, 1 = Monday, ... */
  daysOfWeek?: number[];
  /** For monthly: day of month (1-31) */
  dayOfMonth?: number;
  /** For custom: raw cron expression */
  cronExpression?: string;
  timezone?: string;
}

export interface IdeaToProcessRequest {
  description: string;
  execution_mode?: "MANUAL" | "SEMI_AUTO" | "FULL_AUTO";
  participant_entity_ids?: string[];
  recurrence?: ProcessRecurrence;
}

export interface IdeaToProcessResult {
  status: string;
  process_id: string | null;
  summary: {
    bots_created: number;
    kernels_created: number;
    nodes: number;
    gaps: number;
  };
  created_bots: Array<{
    bot_id: string;
    bot_key: string;
    classification: string;
    kernel_keys: string[];
    status: string;
  }>;
  created_kernels?: Array<{ kernel_key: string; kernel_id: string }>;
  gaps: Array<{
    gap_id: string;
    node_id: string;
    deliverable_id: string;
    description: string;
    recommended_action: string | null;
  }>;
  assessment?: {
    idea_type: string;
    confidence: number;
  };
  questions?: string[];
  message?: string;
}

export interface IdeaToProcessQueuedResponse {
  status: "queued";
  process_id: string;
  call_id: string;
  estimated_duration_sec: number;
  message: string;
}

export type IdeaToProcessResponse = IdeaToProcessResult | IdeaToProcessQueuedResponse;

/**
 * Universal pragmatic intake: the user has an idea but does not declare the
 * output type. The backend assesses internally and defaults to building a
 * process. See project080 T0706 / project052 universal intake.
 */
export async function ideaToAssessToX(
  entityId: string,
  request: IdeaToProcessRequest,
): Promise<ApiResponse<IdeaToProcessResponse>> {
  return apiCall<IdeaToProcessResponse>(`/api/v1/entities/${entityId}/idea_2_assess_2_x`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export interface JobStatusResponse {
  process_id: string;
  call_id: string;
  status: "running" | "completed" | "failed" | "pending_review";
  result: IdeaToProcessResult | null;
  error: string | null;
}

export async function getJobStatus(processId: string): Promise<ApiResponse<JobStatusResponse>> {
  return apiCall<JobStatusResponse>(`/api/v1/jobs/${processId}`);
}
